import {
    Box,
    Button,
    Modal,
    Radio,
    Slide,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    IconWrapper,
    InputField,
    IStyles,
    PrimaryButton,
    pxToRem,
    pxTovW,
    SelectMenu,
    theme,
} from '@geneo2-web/shared-ui';
import { ChangeEvent, ReactNode } from 'react';

const styles: IStyles = {
    root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100vw',
        marginY: { xs: pxToRem(10), md: pxToRem(21), lg: pxTovW(25) },
        boxSizing: 'border-box',
    },
    container: {
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: pxToRem(40), md: pxToRem(18), lg: pxTovW(20) },
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'center', md: 'flex-start' },
        width: { xs: '95%', md: 'unset' },
    },
    contentBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(15), md: pxToRem(32), lg: pxTovW(43) },
        backgroundColor: 'white',
        width: { xs: '95%', md: '85vw', lg: pxTovW(1021) },
        borderRadius: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
        paddingX: { xs: pxToRem(10), md: pxToRem(27), lg: pxTovW(35) },
        paddingY: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
        maxHeight: { xs: '85vh', md: '95vh' },
        overflowY: 'auto',
        boxSizing: 'border-box',
    },
    classSelectBox: {
        borderRadius: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
        border: '1px solid #D2D2D2',
        paddingX: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
        paddingY: { xs: pxToRem(10), md: pxToRem(15), lg: pxTovW(15) },
    },
    classAndSectionContainer: {
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: pxToRem(15), md: pxToRem(21), lg: pxTovW(25) },
        width: '100%',
    },
    classSelectBoxHalf: {
        flex: 1,
        borderRadius: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
        border: '1px solid #D2D2D2',
        paddingX: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
        paddingY: { xs: pxToRem(10), md: pxToRem(15), lg: pxTovW(15) },
    },
    lato27: {
        fontFamily: 'Lato',
        fontSize: { xs: pxToRem(16), md: pxToRem(21), lg: pxTovW(24) },
    },
    radioContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: pxToRem(20), md: pxToRem(54), lg: pxTovW(80) },
    },
    radioDetails: {
        display: 'flex',
        alignItems: 'center',
        gap: { xs: pxToRem(10), md: pxToRem(7), lg: pxTovW(7) },
    },
    radio: {
        color: '#CCE6FE',
        '& .MuiSvgIcon-root': {
            fontSize: { xs: pxToRem(22), md: pxToRem(27), lg: pxTovW(34) },
        },
        '&.MuiRadio-root': {
            padding: '0',
        },
    },
    poppins24: {
        fontFamily: 'poppins',
        fontSize: { xs: pxToRem(12), md: pxToRem(21), lg: pxTovW(24) },
    },
    addMoreBox: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
    },
    textInputField: {
        width: '100%',
        marginTop: { xs: pxToRem(10), md: pxToRem(17), lg: pxTovW(18) },
        '& input': {
            width: '100%',
            height: { xs: pxToRem(40), md: pxToRem(42), lg: pxTovW(60) },
            borderRadius: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
            fontFamily: 'Lato',
            fontSize: { xs: pxToRem(14), md: pxToRem(23), lg: pxTovW(28) },
        },
        '& .MuiOutlinedInput-root': {
            padding: '0',
            border: `${pxTovW(1)} solid #A8D3FC`,
            borderColor: '#A8D3FC',
            backgroundColor: '#DBF5FF',
            borderRadius: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
            paddingX: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
        },
    },
    proceedButton: {
        width: { xs: '50%', md: pxToRem(320), lg: pxTovW(500) },
        height: { xs: pxToRem(35), md: 'unset' },
        fontFamily: 'poppins',
        fontSize: { xs: pxToRem(14), md: pxToRem(25), lg: pxTovW(31) },
        fontWeight: 'bold',
        paddingY: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
        margin: 'auto',
    },
};

interface IOptionList {
    name: string | number;
    id: number | bigint;
    nonEditableItem?: boolean;
    selectedNumber?: number;
    checked?: boolean;
}

interface IProps {
    popupOpen: boolean;
    popupCloseHandler: () => void;
    popupTitle: string;
    chooseClassData: {
        selectedClass: string;
        classList: IOptionList[];
        handleClassChange: (value: string) => void;
        disabled?: boolean;
    };
    chooseSectionData?: {
        selectedSection: string;
        sectionList: IOptionList[];
        handleSectionChange: (value: string) => void;
        disabled?: boolean;
    };
    chooseSubjectData: {
        selectedSubject: string;
        subjectList: IOptionList[];
        handleSubjectChange: (value: string) => void;
        disabled?: boolean;
    };
    nameChangeData: {
        title: string;
        name: string;
        value: string;
        onChange: (event: ChangeEvent<HTMLInputElement>) => void;
        maxLength?: number;
    };
    proceedButtonData: { onClick: () => void; disabled: boolean };
}
export const ProjectSelectionPopup = (props: IProps) => {
    const {
        popupTitle,
        popupOpen,
        popupCloseHandler,
        chooseClassData,
        chooseSectionData,
        chooseSubjectData,
        nameChangeData,
        proceedButtonData,
    } = props;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Modal open={popupOpen} onClose={popupCloseHandler}>
            <Slide
                direction="up"
                in={popupOpen}
                mountOnEnter
                unmountOnExit
                timeout={500}
            >
                <Box sx={styles.root}>
                    <Box sx={styles.container}>
                        <Box sx={styles.contentBox}>
                            <Typography variant={isMobile ? 'h3' : 'h1'} fontWeight="bold" textAlign={'center'}>
                                {popupTitle}
                            </Typography>

                            <Box sx={styles.classAndSectionContainer}>
                                <Box sx={styles.classSelectBoxHalf}>
                                    <Typography variant="bodyText" sx={styles.lato27}>
                                        Choose Class
                                    </Typography>
                                    <SelectMenu
                                        value={chooseClassData.selectedClass}
                                        optionList={chooseClassData.classList}
                                        onChange={chooseClassData.handleClassChange}
                                        disabled={chooseClassData.disabled}
                                        menuPaperProps={{
                                            minWidth: '100px !important',
                                            width: { xs: '90%', md: pxToRem(300), lg: pxTovW(450) },
                                            maxWidth: '100%',
                                        }}
                                        menuProps={{
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                        }}
                                        rootStyles={{
                                            width: '100%',
                                            marginTop: { xs: pxToRem(10), md: pxToRem(17), lg: pxTovW(18) },
                                            height: { xs: pxToRem(40), md: pxToRem(42), lg: pxTovW(60) },
                                        }}
                                    />
                                </Box>

                                {chooseSectionData && (
                                    <Box sx={styles.classSelectBoxHalf}>
                                        <Typography variant="bodyText" sx={styles.lato27}>
                                            Choose Section
                                        </Typography>
                                        <SelectMenu
                                            value={chooseSectionData.selectedSection}
                                            optionList={chooseSectionData.sectionList}
                                            onChange={chooseSectionData.handleSectionChange}
                                            disabled={chooseSectionData.disabled}
                                            menuPaperProps={{
                                                minWidth: '100px !important',
                                                width: { xs: '90%', md: pxToRem(300), lg: pxTovW(450) },
                                                maxWidth: '100%',
                                            }}
                                            menuProps={{
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            rootStyles={{
                                                width: '100%',
                                                marginTop: { xs: pxToRem(10), md: pxToRem(17), lg: pxTovW(18) },
                                                height: { xs: pxToRem(40), md: pxToRem(42), lg: pxTovW(60) },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Box sx={styles.classSelectBoxHalf}>
                                <Typography variant="bodyText" sx={styles.lato27}>
                                    Choose Subject
                                </Typography>
                                <SelectMenu
                                    value={chooseSubjectData?.selectedSubject}
                                    optionList={chooseSubjectData?.subjectList}
                                    onChange={chooseSubjectData?.handleSubjectChange}
                                    disabled={chooseSubjectData?.disabled}
                                    // No fixed width here - MUI sizes the menu's
                                    // min-width to the full-row select.
                                    menuPaperProps={{
                                        minWidth: 'unset',
                                        maxWidth: 'unset',
                                    }}
                                    menuProps={{
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                    }}
                                    rootStyles={{
                                        width: '100%',
                                        marginTop: { xs: pxToRem(10), md: pxToRem(17), lg: pxTovW(18) },
                                        height: { xs: pxToRem(40), md: pxToRem(42), lg: pxTovW(60) },
                                    }}
                                />
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: pxToRem(15), md: pxToRem(21), lg: pxTovW(25) },
                            }}>
                                <Box sx={styles.classSelectBox}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Typography variant="bodyText" sx={styles.lato27}>
                                            {nameChangeData.title}
                                        </Typography>
                                        {nameChangeData.maxLength && (
                                            <Typography variant="subText" color="text.secondary">
                                                {nameChangeData.value.length}/{nameChangeData.maxLength}
                                            </Typography>
                                        )}
                                    </Box>
                                    <InputField
                                        variant="outlined"
                                        fullWidth
                                        type={'text'}
                                        value={nameChangeData.value}
                                        onChange={nameChangeData.onChange}
                                        inputProps={{
                                            maxLength: nameChangeData.maxLength,
                                            'aria-label': nameChangeData.title,
                                        }}
                                        sx={styles.textInputField}
                                    />
                                </Box>

                                <PrimaryButton
                                    variant="contained"
                                    onClick={proceedButtonData.onClick}
                                    sx={styles.proceedButton}
                                    disabled={proceedButtonData.disabled}
                                >
                                    PROCEED
                                </PrimaryButton>
                            </Box>
                        </Box>
                        <Button onClick={popupCloseHandler} sx={{ p: '0px' }}>
                            <IconWrapper
                                name="close-yellow"
                                size="md"
                                parentFolder="icons"
                                type="png"
                                customSx={{
                                    width: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                                    height: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                                }}
                            />
                        </Button>
                    </Box>
                </Box>
            </Slide>
        </Modal>
    );
};
